import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformPanel } from '../../../components/AgentPlatform/AgentPlatformPanel';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { JsonTextArea } from '../../../components/AgentPlatform/JsonTextArea';
import { Button } from '../../../components/UI/Button';
import { useToast } from '../../../components/UI/ToastProvider';
import { AGENT_RUNTIME_IDS, type AgentRuntimeId } from '../../../constants/agentPlatform';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type {
  AgentResourceVisibility,
  AgentToolDto,
  AgentToolType,
} from '../../../types/agentPlatform';
import { apTheme } from '../../../utils/agentPlatformTheme';

type AgentToolFormState = {
  code: string;
  name: string;
  description: string;
  toolType: AgentToolType;
  agentRuntimeId: AgentRuntimeId;
  visibility: AgentResourceVisibility;
  isAsync: boolean;
  inputSchema: string;
  outputSchema: string;
  configuration: string;
};

const emptyCreate = (): AgentToolFormState => ({
  code: '',
  name: '',
  description: '',
  toolType: 'Http' as AgentToolType,
  agentRuntimeId: AGENT_RUNTIME_IDS.httpTool,
  visibility: 'AgentTool' as AgentResourceVisibility,
  isAsync: false,
  inputSchema: '{\n  "type": "object"\n}',
  outputSchema: '{\n  "type": "object"\n}',
  configuration: '{}',
});

export const AgentToolEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState<AgentToolDto | null>(null);
  const [form, setForm] = useState<AgentToolFormState>(emptyCreate());

  useEffect(() => {
    if (isNew || !id) return;
    agentPlatformService
      .getTools()
      .then(tools => {
        const tool = tools.find(t => t.id === id);
        if (!tool) {
          addToast('Ferramenta não encontrada.', 'error');
          return;
        }
        setExisting(tool);
        setForm({
          code: tool.code,
          name: tool.name,
          description: tool.description ?? '',
          toolType: tool.toolType,
          agentRuntimeId:
            tool.toolType === 'Automation'
              ? AGENT_RUNTIME_IDS.nodeRedAutomation
              : tool.toolType === 'Knowledge'
                ? AGENT_RUNTIME_IDS.pgVectorKnowledge
                : AGENT_RUNTIME_IDS.httpTool,
          visibility: tool.visibility,
          isAsync: tool.isAsync,
          inputSchema: tool.inputSchema ?? '{}',
          outputSchema: tool.outputSchema ?? '{}',
          configuration: tool.configuration ?? '{}',
        });
      })
      .finally(() => setLoading(false));
  }, [id, isNew, addToast]);

  const onToolTypeChange = (toolType: AgentToolType) => {
    const runtime: AgentRuntimeId =
      toolType === 'Automation'
        ? AGENT_RUNTIME_IDS.nodeRedAutomation
        : toolType === 'Knowledge'
          ? AGENT_RUNTIME_IDS.pgVectorKnowledge
          : AGENT_RUNTIME_IDS.httpTool;
    setForm(f => ({ ...f, toolType, agentRuntimeId: runtime }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await agentPlatformService.createTool({
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          toolType: form.toolType,
          agentRuntimeId: form.agentRuntimeId,
          visibility: form.visibility,
          isAsync: form.isAsync,
          inputSchema: form.inputSchema,
          outputSchema: form.outputSchema,
          configuration: form.configuration,
        });
        addToast('Ferramenta criada.', 'success');
      } else if (id) {
        await agentPlatformService.updateTool(id, {
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          visibility: form.visibility,
          isAsync: form.isAsync,
          inputSchema: form.inputSchema,
          outputSchema: form.outputSchema,
          configuration: form.configuration,
        });
        addToast('Ferramenta atualizada.', 'success');
      }
      navigate('/agent-platform/tools');
    } catch {
      addToast('Erro ao salvar ferramenta.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-500">Carregando…</div>;
  }

  return (
    <AgentPlatformFeatureGate feature="tools">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
        <AgentPlatformPageHeader
          backTo="/agent-platform/tools"
          backLabel="Ferramentas"
          title={isNew ? 'Nova ferramenta' : `Editar: ${existing?.name ?? form.name}`}
          subtitle={!isNew ? form.code : undefined}
          area="tools"
        />

        <AgentPlatformPanel className="max-w-2xl">
        <form onSubmit={save} className="space-y-4">
          {isNew && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Código</label>
              <input
                className={`${apTheme.input} font-mono`}
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
            <input
              className={apTheme.input}
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
            <textarea
              className={apTheme.input}
              rows={2}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
              <select
                className={apTheme.input}
                value={form.toolType}
                onChange={e => onToolTypeChange(e.target.value as AgentToolType)}
                disabled={!isNew}
              >
                <option value="Http">HTTP</option>
                <option value="Automation">Automação</option>
                <option value="Knowledge">Conhecimento</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Visibilidade</label>
              <select
                className={apTheme.input}
                value={form.visibility}
                onChange={e =>
                  setForm(f => ({ ...f, visibility: e.target.value as AgentResourceVisibility }))
                }
              >
                <option value="AgentTool">AgentTool</option>
                <option value="Tenant">Tenant</option>
                <option value="Private">Private</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isAsync}
              onChange={e => setForm(f => ({ ...f, isAsync: e.target.checked }))}
            />
            Execução assíncrona (callback externo)
          </label>

          <JsonTextArea
            label="Input schema (JSON Schema)"
            value={form.inputSchema}
            onChange={v => setForm(f => ({ ...f, inputSchema: v }))}
            rows={6}
          />
          <JsonTextArea
            label="Output schema"
            value={form.outputSchema}
            onChange={v => setForm(f => ({ ...f, outputSchema: v }))}
            rows={6}
          />
          <JsonTextArea
            label="Configuration"
            value={form.configuration}
            onChange={v => setForm(f => ({ ...f, configuration: v }))}
            rows={6}
          />

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
            <Link to="/agent-platform/tools">
              <Button type="button" variant="secondary">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>
        </AgentPlatformPanel>
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
